import type { Database } from '@/integrations/supabase/types';

type PropFirmRow = Database['public']['Tables']['prop_firms']['Row'];
type FundingProgramRow = Database['public']['Tables']['funding_programs']['Row'];

/**
 * Determines if a firm is eligible to appear in featured placements.
 *
 * Even if is_featured=true in the database, a firm must pass these runtime
 * checks to prevent displaying deals with missing or broken data.
 *
 * Rules (from ACCURACY_SPRINT_TRACKER.md Featured Deals Policy):
 * 1. Must have a working affiliate URL (no monetization = no feature)
 * 2. Must have at least one active funding program with pricing data
 * 3. Must have profit_split defined (critical comparison field)
 */
export function isFeaturedEligible(
  firm: PropFirmRow,
  programs: FundingProgramRow[]
): boolean {
  // Rule 1: Must have an affiliate URL — we can't monetize without one
  if (!firm.affiliate_url || firm.affiliate_url.trim() === '') {
    return false;
  }

  // Rule 2: Must have at least one active program with a price
  const firmPrograms = programs.filter(p => p.prop_firm_id === firm.id);
  const hasPricedProgram = firmPrograms.some(
    p => p.base_price != null || p.discounted_price != null
  );
  if (!hasPricedProgram) {
    return false;
  }

  // Rule 3: Must have profit_split defined (critical comparison field)
  if (!firm.profit_split || firm.profit_split.trim() === '') {
    return false;
  }

  return true;
}

/**
 * Filters an array of firms to only those eligible for featured placement.
 */
export function filterEligibleFirms(
  firms: PropFirmRow[],
  programs: FundingProgramRow[]
): PropFirmRow[] {
  return firms.filter(firm => isFeaturedEligible(firm, programs));
}
